import BulkAddRowsToStructuredTableFlow, {
  BulkAddRowsToStructuredTableFlowProps,
} from 'bulk-import/BulkAddRowsToStructuredTableFlow';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getMaxAllowedRowsToImport} from 'notebook/structured-tables/util/addRowsToTable';
import {Row} from 'notebook/tables/TableConstants';
import {ProcessOutputSchema, ProcessTaskSchema} from 'processes/ProcessConstants';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {BoxSchema, ContainerSchema, PlateSchema} from 'samples/storables/StorableSchemaConstants';
import {Schema} from 'schemas/SchemaConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

import getSpreadsheetColumnTypes from './getSpreadsheetColumnTypes';
import getWritableSchemaFieldIds from './getWritableSchemaFieldIds';
import prepareImportedRows, {ImportedStructuredTableRow} from './prepareImportedRows';

interface Schemas {
  tagSchema?: TagSchema;
  containerSchema?: ContainerSchema;
  resultSchema?: ResultSchema;
  processTaskSchema?: ProcessTaskSchema;
  processOutputSchema?: ProcessOutputSchema;
  plateSchema?: PlateSchema;
  boxSchema?: BoxSchema;
  schemaInterface?: Schema;
}

/**
 * Run through the spreadsheet import flow and return the spreadsheet's rows.
 *
 * Each spreadsheet column is mapped to a table column (or ignored) depending on a few factors:
 * - The columns in the table
 * - For a column that is not a schema field column, what spreadsheet column type it corresponds to
 * - For a schema field column, the corresponding schema and the name of the schema field
 */
export default async function importRowsFromSpreadsheet({
  structuredTable,
  schemas,
  getSpreadsheetTypeForColumn,
}: {
  structuredTable: UnknownStructuredTable;
  schemas: Schemas;
  /**
   * Get the spreadsheet import type for a particular column.
   *
   * Returns `null` if the column does not support spreadsheet import.
   */
  getSpreadsheetTypeForColumn: (
    column: StructuredTableColumn
  ) => SpreadsheetBulkImportConstants.COLUMN_TYPE | null;
}): Promise<Array<Row> | null> {
  const columns = structuredTable.columns;

  const structuredTableAdapter = getStructuredTableAdapterFromTable(structuredTable);
  const columnDefinitions = structuredTableAdapter.getColumnDefinitions(structuredTable);
  const columnDefinitionsForSpreadsheetImport = columnDefinitions.map((columnDefinition, i) => ({
    columnDefinition,
    spreadsheetColumnType: getSpreadsheetTypeForColumn(columns[i]),
  }));
  const spreadsheetColumnTypes = getSpreadsheetColumnTypes(columnDefinitionsForSpreadsheetImport);

  const maxAllowedRowsToImport = getMaxAllowedRowsToImport(structuredTable);

  const writableSchemaFieldIds = [];

  if (schemas.resultSchema) {
    const plf = getPrimaryLinkField(schemas.resultSchema);
    const fields = schemas.resultSchema.allFields.filter((field) => field !== plf);
    writableSchemaFieldIds.push(...getWritableSchemaFieldIds(columns, fields));
  }

  const nonResultSchemas = [
    schemas.containerSchema,
    schemas.plateSchema,
    schemas.boxSchema,
    schemas.processOutputSchema,
    schemas.processTaskSchema,
    schemas.tagSchema,
    schemas.schemaInterface,
  ];
  nonResultSchemas.forEach((schema) => {
    if (schema) {
      writableSchemaFieldIds.push(...getWritableSchemaFieldIds(columns, schema.allFields));
    }
  });

  const serverRows: Array<ImportedStructuredTableRow> | null =
    await legacyRunModal<BulkAddRowsToStructuredTableFlowProps>(BulkAddRowsToStructuredTableFlow, {
      spreadsheetColumnTypes,
      writableSchemaFieldIds,
      containerSchemaId: schemas.containerSchema?.id,
      tagSchemaId: schemas.tagSchema?.id,
      schemaInterfaceId: schemas.schemaInterface?.id,
      resultSchemaId: schemas.resultSchema?.id,
      processTaskSchemaId: schemas.processTaskSchema?.id,
      processOutputSchemaId: schemas.processOutputSchema?.id,
      plateSchemaId: schemas.plateSchema?.id,
      boxSchemaId: schemas.boxSchema?.id,
      modalKey: SpreadsheetBulkImportConstants.SPREADSHEET_BULK_IMPORT_KEY,
      maxAllowedRowsToImport,
    });
  if (serverRows == null) {
    // Modal was closed without submitting
    return null;
  }

  return prepareImportedRows(serverRows, columnDefinitionsForSpreadsheetImport);
}
